import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import './index.css';
import { ThemeProvider } from './components/ThemeProvider';
import { registerSW } from 'virtual:pwa-register';

// Capture the install prompt as early as possible. The browser can fire
// `beforeinstallprompt` before React (and the Install button) has mounted, and
// the event object is only usable if it was captured when it fired. Stash it on
// `window` and announce it so InstallPWA can pick it up whenever it mounts.
// Without this, a fast page load "loses" the event and the Install button falls
// back to manual instructions even on browsers that fully support one-tap install.
(window as any).__installPrompt = (window as any).__installPrompt || null;
window.addEventListener('beforeinstallprompt', (e: Event) => {
  e.preventDefault();
  (window as any).__installPrompt = e;
  window.dispatchEvent(new Event('pwa:installprompt'));
});
window.addEventListener('appinstalled', () => {
  (window as any).__installPrompt = null;
  window.dispatchEvent(new Event('pwa:appinstalled'));
});

if (import.meta.env.DEV) {
  // In development the service worker is disabled (see vite.config.ts). A worker
  // registered by an earlier build would otherwise keep serving a stale JS
  // bundle — making code changes look like they had no effect. Proactively
  // unregister any leftover worker and drop its caches so dev always runs the
  // latest code.
  if ('serviceWorker' in navigator) {
    // A worker controlling THIS load already served its cached (stale) bundle,
    // so unregistering alone isn't enough — the page keeps showing old code.
    // After clearing it, reload once (guarded against loops) to fetch fresh JS.
    const controlled = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.getRegistrations().then((regs) => {
      return Promise.all(regs.map((r) => r.unregister()));
    }).then(() => {
      const key = 'dev:swReloaded';
      if (controlled && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
      }
    }).catch(() => undefined);
  }
  if ('caches' in window) {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => undefined);
  }
} else {
  // Production: register the PWA service worker for offline capability & auto-updates.
  // autoUpdate (see vite.config.ts) swaps in the new worker and reloads silently —
  // no "reload to apply" prompt is shown to the user.
  registerSW({ immediate: true });
}

// Safety net for the post-deploy window: if a lazy-loaded chunk 404s because this
// tab is still holding a stale build, reload once to fetch the fresh assets
// instead of surfacing an "unable to load" error. Throttled to avoid a reload loop.
window.addEventListener('vite:preloadError', () => {
  const key = 'pwa:lastChunkReload';
  const last = Number(sessionStorage.getItem(key) || 0);
  if (Date.now() - last > 10_000) {
    sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
  }
});

// Initialize QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {

      retry: 1,
    },
  },
});

// Error Boundary for unhandled errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Unhandled error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg-900">
          <div className="text-center">
            <h1 className="mb-4 font-display text-page-title text-destructive">Oops!</h1>
            <p className="text-text-400 mb-6">Something went wrong. Please refresh the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary-700 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Render App
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <App />
          </ThemeProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
